import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, DeviceLogEntry } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import {
  Search,
  Bug,
  Smartphone,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Filter,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  User,
  Settings,
  Activity,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface User {
  id: string
  username: string
  is_active: boolean
  last_seen_at?: string
  last_device_model?: string
  last_app_version?: string
}

const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const
type LogLevel = typeof LOG_LEVELS[number]

const levelColors: Record<LogLevel, string> = {
  debug: 'text-muted-foreground',
  info: 'text-blue-500',
  warn: 'text-yellow-500',
  error: 'text-red-500',
}

const levelIcons: Record<LogLevel, React.ReactNode> = {
  debug: <Bug className="w-4 h-4" />,
  info: <Info className="w-4 h-4" />,
  warn: <AlertTriangle className="w-4 h-4" />,
  error: <XCircle className="w-4 h-4" />,
}

export default function DeviceLogs() {
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [levelFilter, setLevelFilter] = useState<string>('')
  const [tagFilter, setTagFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('24h')
  const queryClient = useQueryClient()

  // Fetch users for selection
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', userSearch],
    queryFn: () => api.getUsers({ page: 1, per_page: 50, search: userSearch }),
  })

  // Fetch selected user's settings
  const { data: userSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['user-client-settings', selectedUserId],
    queryFn: () => api.getUserClientSettings(selectedUserId!),
    enabled: !!selectedUserId,
  })

  // Fetch user's log tags
  const { data: tagsData } = useQuery({
    queryKey: ['user-log-tags', selectedUserId],
    queryFn: () => api.getUserLogTags(selectedUserId!),
    enabled: !!selectedUserId,
  })

  // Calculate time range
  const getTimeRange = () => {
    const now = new Date()
    const since = new Date()
    switch (timeRange) {
      case '1h':
        since.setHours(since.getHours() - 1)
        break
      case '6h':
        since.setHours(since.getHours() - 6)
        break
      case '24h':
        since.setDate(since.getDate() - 1)
        break
      case '7d':
        since.setDate(since.getDate() - 7)
        break
    }
    return { since: since.toISOString(), until: now.toISOString() }
  }

  // Fetch logs
  const { data: logsData, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['user-device-logs', selectedUserId, levelFilter, tagFilter, searchFilter, timeRange],
    queryFn: () => {
      const { since, until } = getTimeRange()
      return api.getUserDeviceLogs({
        userId: selectedUserId!,
        level: levelFilter || undefined,
        tag: tagFilter || undefined,
        search: searchFilter || undefined,
        since,
        until,
        limit: 500,
      })
    },
    enabled: !!selectedUserId,
    refetchInterval: userSettings?.settings.debug_logging ? 5000 : false,
  })

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: { debug_logging?: boolean; log_level?: string }) =>
      api.updateUserClientSettings(selectedUserId!, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-client-settings', selectedUserId] })
    },
  })

  // Delete logs mutation
  const deleteLogsMutation = useMutation({
    mutationFn: () => api.deleteUserLogs(selectedUserId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-device-logs', selectedUserId] })
      queryClient.invalidateQueries({ queryKey: ['user-client-settings', selectedUserId] })
    },
  })

  const users: User[] = usersData?.data || []
  const logs: DeviceLogEntry[] = logsData?.logs || []
  const tags: string[] = tagsData?.tags || []

  const selectedUser = users.find(u => u.id === selectedUserId)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Device Logs</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* User Selection Panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-card rounded-lg border border-border p-4">
            <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Select User
            </h2>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="input-field pl-10 text-sm"
              />
            </div>

            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {usersLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg transition-colors',
                      selectedUserId === user.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{user.username}</span>
                      <span className={cn(
                        'text-xs',
                        selectedUserId === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      )}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {user.last_device_model && (
                      <div className={cn(
                        'text-xs mt-0.5',
                        selectedUserId === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      )}>
                        {user.last_device_model}
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {selectedUserId ? (
            <>
              {/* User Settings Card */}
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Debug Settings - {userSettings?.username || selectedUser?.username}
                  </h2>
                  {settingsLoading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  )}
                </div>

                {userSettings && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Debug Logging Toggle */}
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">Debug Logging</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userSettings.settings.debug_logging}
                            onChange={(e) => updateSettingsMutation.mutate({ debug_logging: e.target.checked })}
                            className="sr-only peer"
                            disabled={updateSettingsMutation.isPending}
                          />
                          <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {userSettings.settings.debug_logging ? 'Enabled - auto-disables after 24h' : 'Disabled'}
                      </p>
                    </div>

                    {/* Log Level */}
                    <div className="bg-muted/50 rounded-lg p-4">
                      <span className="text-sm font-medium text-foreground block mb-2">Log Level</span>
                      <select
                        value={userSettings.settings.log_level}
                        onChange={(e) => updateSettingsMutation.mutate({ log_level: e.target.value })}
                        className="input-field text-sm py-1"
                        disabled={updateSettingsMutation.isPending}
                      >
                        {LOG_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Device Info */}
                    <div className="bg-muted/50 rounded-lg p-4">
                      <span className="text-sm font-medium text-foreground block mb-2 flex items-center gap-1">
                        <Smartphone className="w-4 h-4" /> Device
                      </span>
                      {userSettings.device?.model ? (
                        <div className="text-sm text-muted-foreground">
                          <p>{userSettings.device.manufacturer} {userSettings.device.model}</p>
                          <p className="text-xs">v{userSettings.device.app_version} ({userSettings.device.app_build})</p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No device info</p>
                      )}
                    </div>

                    {/* Log Count & Last Seen */}
                    <div className="bg-muted/50 rounded-lg p-4">
                      <span className="text-sm font-medium text-foreground block mb-2 flex items-center gap-1">
                        <Activity className="w-4 h-4" /> Activity
                      </span>
                      <div className="text-sm text-muted-foreground">
                        <p>{userSettings.logCount?.toLocaleString() || 0} logs stored</p>
                        {userSettings.lastSeen && (
                          <p className="text-xs">Last seen: {formatDate(userSettings.lastSeen)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Logs Card */}
              <div className="bg-card rounded-lg border border-border">
                {/* Logs Header */}
                <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-medium text-foreground">Logs</h3>
                    <div className="flex items-center gap-1">
                      {(['1h', '6h', '24h', '7d'] as const).map((range) => (
                        <button
                          key={range}
                          onClick={() => setTimeRange(range)}
                          className={cn(
                            'px-2 py-1 text-xs rounded transition-colors',
                            timeRange === range
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={cn(
                        'btn-secondary text-sm flex items-center gap-1',
                        showFilters && 'bg-primary/10'
                      )}
                    >
                      <Filter className="w-4 h-4" />
                      Filters
                      {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => refetchLogs()}
                      className="btn-secondary text-sm"
                      disabled={logsLoading}
                    >
                      <RefreshCw className={cn('w-4 h-4', logsLoading && 'animate-spin')} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Delete all logs for this user? This cannot be undone.')) {
                          deleteLogsMutation.mutate()
                        }
                      }}
                      className="btn-secondary text-sm text-destructive hover:bg-destructive/10"
                      disabled={deleteLogsMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Filters */}
                {showFilters && (
                  <div className="px-4 py-3 border-b border-border bg-muted/30 flex flex-wrap gap-3">
                    <select
                      value={levelFilter}
                      onChange={(e) => setLevelFilter(e.target.value)}
                      className="input-field text-sm py-1.5 w-auto"
                    >
                      <option value="">All Levels</option>
                      {LOG_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </option>
                      ))}
                    </select>

                    <select
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      className="input-field text-sm py-1.5 w-auto"
                    >
                      <option value="">All Tags</option>
                      {tags.map((tag) => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>

                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search logs..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="input-field pl-10 text-sm py-1.5"
                      />
                    </div>
                  </div>
                )}

                {/* Logs List */}
                <div className="max-h-[600px] overflow-y-auto">
                  {logsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : logs.length > 0 ? (
                    <div className="divide-y divide-border">
                      {logs.map((log, index) => (
                        <div key={index} className="px-4 py-2 hover:bg-muted/30 transition-colors">
                          <div className="flex items-start gap-3">
                            <span className={cn('mt-0.5', levelColors[log.level as LogLevel])}>
                              {levelIcons[log.level as LogLevel] || <AlertCircle className="w-4 h-4" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                <span>{new Date(log.timestamp).toLocaleString()}</span>
                                <span className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
                                  {log.tag}
                                </span>
                              </div>
                              <p className="text-sm text-foreground font-mono break-all">
                                {log.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <AlertCircle className="w-12 h-12 mb-3 opacity-50" />
                      <p className="text-base">No logs found</p>
                      <p className="text-sm mt-1">
                        {userSettings?.settings.debug_logging
                          ? 'Waiting for logs from the app...'
                          : 'Enable debug logging to start collecting logs'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Logs Footer */}
                {logs.length > 0 && (
                  <div className="px-4 py-2 border-t border-border bg-muted/30 text-sm text-muted-foreground">
                    Showing {logs.length} of {logsData?.total || 0} logs
                    {logsData?.hasMore && ' (more available)'}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">Select a User</h3>
              <p className="text-muted-foreground">
                Choose a user from the list to view their debug settings and device logs
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
